/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the drugShipmentDepotBatch management page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions, Grid } from '@material-ui/core';

import * as Yup from 'yup';

import { Field, FormRenderProps } from 'react-final-form';

import { useSnackbar } from 'notistack';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../../api/Neon.Dtos';
import { AsyncForm } from '../../../components/FinalForm/AsyncForm';
import { DrugShipmentDepotBatchActions } from '../../../store/modules/drugmanagement/drugshipmentdepotbatch';
import TextFieldWrapper from '../../../components/FinalForm/FinalFormControls/TextFieldWrapper';
import NumberTextField from '../../../components/FinalForm/FinalFormControls/NumberTextField';
import { getDrugUnitName } from '../../../utilities/getDrugUnitName';
import useAsyncFunction from '../../../hooks/useAsyncFunction';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDrugShipmentDepotBatchModalProps {
    drugShipmentDepotBatch: Dtos.DrugShipmentDepotBatch | null;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IDrugShipmentDepotBatchFormProps extends FormRenderProps<Dtos.DrugShipmentDepotBatch> {
    drugShipmentDepotBatch: Dtos.DrugShipmentDepotBatch | null;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

interface IDrugShipmentDepotBatchForm extends Partial<Dtos.DrugShipmentDepotBatch> {
}

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const drugUnitName = getDrugUnitName(true);

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const validation = Yup.object().shape<IDrugShipmentDepotBatchForm>({
    drugUnits: Yup.number().required()
        .when(['drugUnitsUsed', 'drugUnitsLost', 'drugUnitsDamaged', 'drugUnitsDestroyed', 'drugUnitsQuarantined'],
            (drugUnitsUsed: number, drugUnitsLost: number, drugUnitsDamaged: number, drugUnitsDestroyed: number, drugUnitsQuarantined: number, schema: Yup.NumberSchema) => {
                return schema.min(drugUnitsUsed + drugUnitsLost + drugUnitsDamaged + drugUnitsDestroyed + drugUnitsQuarantined, `${drugUnitName} total can not be less than the sum of the rest of the drug unit values`);
            }),
    drugUnitsUsed: Yup.number().required().min(0),
    drugUnitsLost: Yup.number().required().min(0),
    drugUnitsDamaged: Yup.number().required().min(0),
    drugUnitsDestroyed: Yup.number().required().min(0),
    drugUnitsQuarantined: Yup.number().required().min(0)
});

const DrugShipmentDepotBatchForm: React.FunctionComponent<IDrugShipmentDepotBatchFormProps> = ({
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            const errorMessage = error.message ?
                error.message :
                'Unknown error';

            enqueueSnackbar(`An error occurred while attempting to save the drug shipment: ${errorMessage}`, { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    return (
        <>
            <DialogContent>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnits"
                            component={NumberTextField}
                            label={`Total ${drugUnitName}`}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnitsUsed"
                            component={NumberTextField}
                            label={`${drugUnitName} Used`}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnitsDamaged"
                            component={NumberTextField}
                            label={`${drugUnitName} Damaged`}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnitsDestroyed"
                            component={NumberTextField}
                            label={`${drugUnitName} Destroyed`}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnitsLost"
                            component={NumberTextField}
                            label={`${drugUnitName} Lost`}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnitsQuarantined"
                            component={NumberTextField}
                            label={`${drugUnitName} Quarantined`}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="notes"
                            component={TextFieldWrapper}
                            type="text"
                            label="Notes"
                            disabled={formDisabled}
                            multiline={true}
                            rows={4}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const DrugShipmentDepotBatchModal: React.FunctionComponent<IDrugShipmentDepotBatchModalProps> = ({
    drugShipmentDepotBatch,
    modalOpen,
    setModalOpen,
    onCloseClick,
    onFormSave
}) => {

    const drugShipmentDepotBatchForm = React.useCallback((props: FormRenderProps<Dtos.DrugShipmentDepotBatch>) => {
        return (
            <DrugShipmentDepotBatchForm
                drugShipmentDepotBatch={drugShipmentDepotBatch}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [setModalOpen, drugShipmentDepotBatch, onFormSave]);

    const asyncSave = useAsyncFunction(DrugShipmentDepotBatchActions.save, DrugShipmentDepotBatchActions.saveSuccess, DrugShipmentDepotBatchActions.saveFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Save Drug Shipment
                </DialogTitle>
                <AsyncForm
                    initialValues={drugShipmentDepotBatch}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={drugShipmentDepotBatchForm}
                />
            </Dialog>
        </>
    );
}

export default DrugShipmentDepotBatchModal;